import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ArrowLeft, Save, Globe, Eye } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AIActorPublicProfile() {
  const { actorId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    is_public: false,
    public_profile_visibility: {
      experience_level: true,
      about: true,
      work_profile: true,
      background: true,
      skills: true,
      responsibilities: true,
      industries_served: true,
      languages: true,
      jobs_completed: true,
      success_rate: true,
      response_time: true,
      verified_badges: true,
      video_url: true,
      customer_reviews: true,
      case_studies: true,
      projects: true,
      revenue_generated: true,
      time_saved_stats: true,
      credentials: true,
      pricing: true,
      packages: true
    }
  });

  useEffect(() => {
    fetchActorData();
  }, [actorId]);

  const fetchActorData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/ai/actors/${actorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const actor = response.data;
      setFormData({
        is_public: actor.is_public || false,
        public_profile_visibility: actor.public_profile_visibility || {
          experience_level: true,
          about: true,
          work_profile: true,
          background: true,
          skills: true,
          responsibilities: true,
          industries_served: true,
          languages: true,
          jobs_completed: true,
          success_rate: true,
          response_time: true,
          verified_badges: true,
          video_url: true,
          customer_reviews: true,
          case_studies: true,
          projects: true,
          revenue_generated: true,
          time_saved_stats: true,
          credentials: true,
          pricing: true,
          packages: true
        }
      });
    } catch (error) {
      toast.error("Failed to fetch actor data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      
      // CRITICAL: Fetch current actor data to avoid overwriting other fields
      const currentResponse = await axios.get(`${API}/ai/actors/${actorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const currentActor = currentResponse.data;
      
      // Merge current actor data with our changes
      const mergedData = {
        ...currentActor,                                    // Keep all existing fields
        is_public: formData.is_public,                      // Update only is_public
        public_profile_visibility: formData.public_profile_visibility  // Update only visibility
      };
      
      // Save merged data
      await axios.put(`${API}/ai/actors/${actorId}`, mergedData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Public profile settings updated successfully");
    } catch (error) {
      toast.error("Failed to update public profile settings");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading public profile settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/ai/actors/manage/${actorId}`)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Manage
        </Button>
        <h1 className="text-2xl font-bold">Public Profile Settings</h1>
        <Button onClick={handleSave} disabled={saving}>
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      <div className="space-y-6">
        {/* Make Profile Public Toggle */}
                {/* Public Profile Toggle */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-indigo-600" />
                    <div>
                      <Label className="text-base font-semibold">Make Profile Public</Label>
                      <p className="text-xs text-slate-500 mt-1">Allow anyone to view this actor's profile without login</p>
                    </div>
                  </div>
                  <Switch
                    checked={formData.is_public}
                    onCheckedChange={(checked) => setFormData({...formData, is_public: checked})}
                  />
                </div>

        {/* Public Profile Visibility Settings */}
                {/* Public Profile Visibility Settings */}
                {formData.is_public && (
                  <Card className="border-2 border-blue-200 bg-blue-50/30">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-blue-900">
                        <Eye className="w-5 h-5" />
                        Public Profile Visibility
                      </CardTitle>
                      <CardDescription>
                        Control which fields are visible on the public profile page
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        {/* Basic Information */}
                        <div className="col-span-2">
                          <Label className="text-sm font-semibold text-slate-700 mb-2 block">Basic Information</Label>
                        </div>
                        
                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Experience Level</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.experience_level ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                experience_level: checked
                              }
                            })}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">About</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.about ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                about: checked
                              }
                            })}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Work Profile</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.work_profile ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                work_profile: checked
                              }
                            })}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Background</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.background ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                background: checked
                              }
                            })}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Skills</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.skills ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                skills: checked
                              }
                            })}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Responsibilities</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.responsibilities ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                responsibilities: checked
                              }
                            })}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Industries Served</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.industries_served ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                industries_served: checked
                              }
                            })}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Languages</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.languages ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                languages: checked
                              }
                            })}
                          />
                        </div>

                        {/* Performance & Metrics */}
                        <div className="col-span-2 mt-4">
                          <Label className="text-sm font-semibold text-slate-700 mb-2 block">Performance & Metrics</Label>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Jobs Completed</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.jobs_completed ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                jobs_completed: checked
                              }
                            })}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Success Rate</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.success_rate ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                success_rate: checked
                              }
                            })}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Response Time</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.response_time ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                response_time: checked
                              }
                            })}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Verified Badges</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.verified_badges ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                verified_badges: checked
                              }
                            })}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Video</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.video_url ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                video_url: checked
                              }
                            })}
                          />
                        </div>

                        {/* Social Proof */}
                        <div className="col-span-2 mt-4">
                          <Label className="text-sm font-semibold text-slate-700 mb-2 block">Social Proof & Portfolio</Label>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Customer Reviews</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.customer_reviews ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                customer_reviews: checked
                              }
                            })}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Case Studies</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.case_studies ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                case_studies: checked
                              }
                            })}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Projects Portfolio</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.projects ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                projects: checked
                              }
                            })}
                          />
                        </div>

                        {/* Trust Indicators */}
                        <div className="col-span-2 mt-4">
                          <Label className="text-sm font-semibold text-slate-700 mb-2 block">Trust & Achievements</Label>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Revenue Generated</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.revenue_generated ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                revenue_generated: checked
                              }
                            })}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Time Saved Stats</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.time_saved_stats ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                time_saved_stats: checked
                              }
                            })}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Credentials</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.credentials ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                credentials: checked
                              }
                            })}
                          />
                        </div>

                        {/* Pricing */}
                        <div className="col-span-2 mt-4">
                          <Label className="text-sm font-semibold text-slate-700 mb-2 block">Pricing Information</Label>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Hourly Rate</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.pricing ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                pricing: checked
                              }
                            })}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Pricing Packages</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.packages ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                packages: checked
                              }
                            })}
                          />
                        </div>
                      </div>

                      <div className="mt-4 p-3 bg-blue-100 rounded border border-blue-200">
                        <p className="text-xs text-blue-800">
                          <strong>Note:</strong> Name and tagline are always visible on the public profile. Use these toggles to show/hide additional fields.
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}
      </div>
    </div>
  );
}
